import type { FC } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import CircularGauge from 'app/components/Gauges/CircularGauge';
import { withBackgroundImage } from 'app/utils/style';
import { isNumber } from 'app/utils/types';
import StatLineGauge from 'app/components/Gauges/StatLineGauge';
import { localizedMapName } from 'app/i18n/utils';
import { getCDNUrl } from 'app/utils/api_url';

import style from './style.module.scss';
import { getMapStats } from './utils';

interface Props {
  data: MapsWinrate;
  selectCallback: () => void;
  expanded: boolean;
  opponent1Id: number | undefined;
  opponent2Id: number | undefined;
  videogame: VideogameSlug;
}

const MapStats: FC<Props> = ({
  data,
  selectCallback,
  expanded,
  opponent1Id,
  opponent2Id,
  videogame
}) => {
  const { t } = useTranslation();

  const opponent1 = opponent1Id ? data.opponents[opponent1Id] : undefined;
  const opponent2 = opponent2Id ? data.opponents[opponent2Id] : undefined;

  const mapWinrateOpponent1 = opponent1
    ? Math.round((opponent1.wins / opponent1.total) * 100)
    : null;
  const mapWinrateOpponent2 = opponent2
    ? Math.round((opponent2.wins / opponent2.total) * 100)
    : null;

  const expandContainerClassname = classNames(style.expandContainer, {
    [style.expanded]: expanded
  });

  const lines = getMapStats(videogame, opponent1, opponent2);

  return (
    <div className={style.mapStats}>
      <button
        onClick={selectCallback}
        style={withBackgroundImage(getCDNUrl(data.image_url))}
        className={style.mapHeader}
        type="button"
        disabled={lines.length === 0}
      >
        {isNumber(mapWinrateOpponent1) ? (
          <span className={style.mapWinrate}>
            <CircularGauge
              value={mapWinrateOpponent1}
              weak={!!mapWinrateOpponent2 && mapWinrateOpponent1 < mapWinrateOpponent2}
              className={style.mapWinrateGauge}
              valueClassName={
                mapWinrateOpponent1 === 100
                  ? style.mapWinrateGaugeText100
                  : style.mapWinrateGaugeText
              }
            />
            <span className={style.mapWinrateText}>
              {t('blocks_teams-stats_map-number', { number: opponent1?.total ?? '' })}
            </span>
          </span>
        ) : (
          <span className={style.mapWinrateText}>{t('blocks_teams-stats_not-played')}</span>
        )}

        <span className={style.mapName}>{localizedMapName(data, t)}</span>

        {isNumber(mapWinrateOpponent2) ? (
          <span className={style.mapWinrate}>
            <span className={style.mapWinrateText}>
              {t('blocks_teams-stats_map-number', { number: opponent2?.total ?? '' })}
            </span>
            <CircularGauge
              value={mapWinrateOpponent2}
              weak={!!mapWinrateOpponent1 && mapWinrateOpponent2 < mapWinrateOpponent1}
              className={style.mapWinrateGauge}
              valueClassName={
                mapWinrateOpponent2 === 100
                  ? style.mapWinrateGaugeText100
                  : style.mapWinrateGaugeText
              }
            />
          </span>
        ) : (
          <span className={style.mapWinrateText}>{t('blocks_teams-stats_not-played')}</span>
        )}
      </button>
      <div className={expandContainerClassname}>
        <StatLineGauge lines={lines} translationKey={`blocks_teams-stats_videogame_${videogame}`} />
      </div>
    </div>
  );
};

MapStats.displayName = 'MapStats';

export default MapStats;
